import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, CircularProgress, Dialog, DialogContent, Grid, Typography } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import { countBy, uniq } from 'lodash'
import moment from 'moment'
import { toast } from 'react-toastify'
import { aquaFetch } from 'common/utils/ActionUtils'
import ApplicationConf from 'conf/ApplicationConf'
import { CardTitle } from 'common/components/card/Card'
import { CardTable, SORT } from 'common/components/datatable/Table'
import { BasicDialogTitle } from 'common/components/modal/DialogComponents'
import useBoolean from 'common/hooks/useBoolean'
import { STATUS } from 'common/constants/storeConstants'
import { CATEGORIES_LABELS, STATUS_TICKETS_LABEL } from 'common/constants/Constants'
import { TICKET_HEADERS, formatRow } from './CustomerTicketsApp'

// Statuts « ouverts » (état courant < 60) — aligné sur Suivi des lots / Tickets client.
const OPEN_STATUS = [
    STATUS_TICKETS_LABEL.NOUVEAU,
    STATUS_TICKETS_LABEL.AFFECTE,
    STATUS_TICKETS_LABEL.COMMENTAIRE,
    STATUS_TICKETS_LABEL.EN_COURS,
    STATUS_TICKETS_LABEL.REOUVERT,
]

// Palette par catégorie (ordre de CATEGORIES_LABELS) pour les barres du graphe.
const CATEGORY_COLORS = ['#35609f', '#e65100', '#2e7d32', '#6a1b9a', '#00838f', '#c2185b', '#f9a825', '#546e7a']
const categoryColor = (category) => CATEGORY_COLORS[Math.max(0, CATEGORIES_LABELS.indexOf(category)) % CATEGORY_COLORS.length]

const CustomerTicketsStatsCard = ({ customer, projects = [] }) => {
    // Paramètres de toutes les instances du client (pour récupérer les MANTIS_NAME).
    const [instancesParams, setInstancesParams] = useState([])
    const [tickets, setTickets] = useState([])
    const [status, setStatus] = useState(STATUS.IDLE)

    useEffect(() => {
        setInstancesParams([])
        customer?.instances?.forEach(i => {
            aquaFetch(ApplicationConf.instance.single(i.instance)).then(inst => setInstancesParams(prev => [...prev, ...(inst?.parameters ?? [])]))
        })
    }, [customer])

    // Projet(s) Mantis du client : union des MANTIS_NAME (ids séparés par des virgules) de ses instances.
    const projectIds = useMemo(
        () => uniq(instancesParams.filter(p => p.parameter === 'MANTIS_NAME').flatMap(p => p.value?.split(',').map(v => Number(v)) ?? [])),
        [instancesParams],
    )
    const projectNames = useMemo(
        () => projects.filter(p => projectIds.includes(p.id)).map(p => p.name),
        [projects, projectIds],
    )
    const projectNamesKey = projectNames.join(',')

    useEffect(() => {
        if (!projectNames.length) {
            setTickets([])
            return
        }
        setStatus(STATUS.LOADING)
        aquaFetch(ApplicationConf.support.statisticsCreated(), { method: 'POST', body: { projects: projectNames } })
            .then(res => {
                setTickets(res ?? [])
                setStatus(STATUS.SUCCEEDED)
            })
            .catch(() => {
                setStatus(STATUS.REJECTED)
                toast.error('Erreur lors du chargement des tickets')
            })
    }, [projectNames, projectNamesKey])

    const { lateTickets, categoryData } = useMemo(() => {
        const now = moment().unix()
        const open = tickets.filter(t => OPEN_STATUS.includes(t.status))
        const late = open.filter(t => t.dueDate && t.dueDate > 1 && t.dueDate < now)
        const counts = countBy(open, t => t.category || 'Sans catégorie')
        const data = Object.entries(counts)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
        return { lateTickets: late, categoryData: data }
    }, [tickets])
    const lateCount = lateTickets.length

    const { value: detailOpen, setTrue: openDetail, setFalse: closeDetail } = useBoolean(false)

    const chartOptions = {
        grid: { top: 24, right: 16, bottom: 8, left: 8, containLabel: true },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        xAxis: {
            type: 'category',
            data: categoryData.map(d => d.name),
            axisLabel: { interval: 0, rotate: 30, fontSize: 10, hideOverlap: false },
        },
        yAxis: { type: 'value', minInterval: 1 },
        series: [{
            type: 'bar',
            data: categoryData.map(d => ({ value: d.value, itemStyle: { color: categoryColor(d.name) } })),
            label: { show: true, position: 'top', fontSize: 10 },
        }],
    }

    // Tant que les paramètres des instances (ou les tickets) se chargent, on affiche un spinner.
    const resolving = (customer?.instances?.length ?? 0) > 0 && !instancesParams.length
    const loading = status === STATUS.LOADING || resolving

    return (
        <Card>
            <CardTitle
                title={`Tickets (${lateCount} en retard)`}
                actions={[{
                    icon: 'visibility',
                    tooltip: 'Voir le détail',
                    onClick: openDetail,
                    displayed: lateCount > 0,
                }]}
            />
            <CardContent>
                {loading ? (
                    <Grid container justifyContent='center' sx={{ padding: 2 }}>
                        <CircularProgress />
                    </Grid>
                ) : !projectNames.length ? (
                    <Typography color='textSecondary'>Aucun projet Mantis rattaché aux instances du client.</Typography>
                ) : (
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Typography sx={{ fontWeight: 'bold', fontSize: 13 }}>Tickets ouverts par catégorie</Typography>
                            {categoryData.length ? (
                                <ReactECharts option={chartOptions} notMerge lazyUpdate style={{ height: '150px' }} />
                            ) : (
                                <Typography color='textSecondary'>Aucun ticket ouvert.</Typography>
                            )}
                        </Grid>
                    </Grid>
                )}
            </CardContent>
            <Dialog open={detailOpen} maxWidth='xl' fullWidth>
                <BasicDialogTitle onClose={closeDetail}>
                    {`Tickets en retard (${lateCount})`}
                </BasicDialogTitle>
                <DialogContent>
                    <CardTable
                        title='Tickets en retard'
                        color='#161832'
                        headers={TICKET_HEADERS}
                        rows={lateTickets.map(formatRow)}
                        rowsPerPageDefault={25}
                        defaultSort={{ column: 'echeance', direction: SORT.ASC }}
                        onClickRow={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
                    />
                </DialogContent>
            </Dialog>
        </Card>
    )
}

CustomerTicketsStatsCard.propTypes = {
    customer: PropTypes.shape({
        instances: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    projects: PropTypes.arrayOf(PropTypes.shape({})),
}

export { CustomerTicketsStatsCard }
export default CustomerTicketsStatsCard
